import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { postData, putData } from '../../webService/webservice';
import { toast } from 'react-toastify';
import './TemplateEditorModal.css';

const PLACEHOLDERS = [
  '{{1}}',
  '{{2}}',
  '{{3}}',
  '{{CustomerName}}',
  '{{StoreName}}',
  '{{InvoiceNumber}}',
  '{{BillAmount}}',
  '{{OfferCode}}',
  '{{CurrentDate}}'
];

const META_CATEGORIES = [
  { code: 'MARKETING', label: 'Marketing (Promotions, Offers, News)' },
  { code: 'UTILITY', label: 'Utility (Order Updates, Receipts, Reminders)' },
  { code: 'AUTHENTICATION', label: 'Authentication (OTPs, Security Codes)' }
];

const LANGUAGES = [
  { code: 'en_US', label: 'English (US)' },
  { code: 'en_GB', label: 'English (UK)' },
  { code: 'hi', label: 'Hindi (hi)' },
  { code: 'es', label: 'Spanish (es)' },
  { code: 'fr', label: 'French (fr)' },
  { code: 'pt_BR', label: 'Portuguese (pt_BR)' }
];

const TemplateEditorModal = ({ isOpen, onClose, template = null, onSaved, storeId }) => {
  const [templateName, setTemplateName] = useState('');
  const [metaCategory, setMetaCategory] = useState('MARKETING');
  const [language, setLanguage] = useState('en_US');
  const [headerType, setHeaderType] = useState('NONE');
  const [headerText, setHeaderText] = useState('');
  const [mediaType, setMediaType] = useState('None');
  const [mediaUrl, setMediaUrl] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [buttons, setButtons] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [metaError, setMetaError] = useState('');

  useEffect(() => {
    setMetaError('');
    if (template) {
      setTemplateName(template.templateName || '');
      setMetaCategory(template.metaCategory || 'MARKETING');
      setLanguage(template.language || 'en_US');
      setHeaderType(template.headerType || 'NONE');
      setHeaderText(template.headerText || '');
      setMediaType(template.mediaType || 'None');
      setMediaUrl(template.mediaUrl || '');
      setBodyText(template.bodyText || '');
      setFooterText(template.footerText || '');
      setButtons(template.buttons || []);
    } else {
      setTemplateName('');
      setMetaCategory('MARKETING');
      setLanguage('en_US');
      setHeaderType('NONE');
      setHeaderText('');
      setMediaType('None');
      setMediaUrl('');
      setBodyText('Hello {{1}},\n\nSpecial offer for you from {{2}}! Use code {{3}} for exclusive discounts.\n\nThank you!');
      setFooterText('Terms & Conditions apply.');
      setButtons([]);
    }
  }, [template, isOpen]);

  // Real-time Meta Name Formatter
  const handleNameChange = (e) => {
    const rawVal = e.target.value;
    const formatted = rawVal
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/_+/g, '_');
    setTemplateName(formatted);
  };

  const insertPlaceholder = (ph) => {
    setBodyText((prev) => `${prev} ${ph}`);
  };

  const addButton = () => {
    if (buttons.length >= 3) {
      toast.warning('Maximum 3 buttons allowed per template');
      return;
    }
    setButtons([...buttons, { type: 'QUICK_REPLY', text: 'Interested', url: '', phoneNumber: '', code: '' }]);
  };

  const updateButton = (index, field, val) => {
    const updated = [...buttons];
    updated[index][field] = val;
    setButtons(updated);
  };

  const removeButton = (index) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setMetaError('');

    if (!templateName.trim()) {
      toast.error('Please enter a Template Name');
      return;
    }
    if (!bodyText.trim()) {
      toast.error('Please enter Template Body Text');
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
      const payload = {
        templateName: templateName.trim(),
        metaCategory,
        language,
        headerType,
        headerText,
        mediaType,
        mediaUrl,
        bodyText,
        footerText,
        buttons,
        storeId
      };

      let res;
      if (template?._id) {
        res = await putData(`templates/${template._id}`, payload, token);
      } else {
        res = await postData('templates', payload, token);
      }

      if (res?.status === 200 || res?.success || res?.data) {
        toast.success(res.message || `WhatsApp Template created & submitted to Meta successfully!`);
        if (onSaved) onSaved();
        onClose();
      } else {
        const errorMsg = res?.message || 'Failed to submit template to Meta WABA';
        setMetaError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('Error saving template:', err);
      const errDetail = err?.response?.data?.message || err?.message || 'Server error creating template';
      setMetaError(errDetail);
      toast.error(errDetail);
    } finally {
      setLoading(false);
    }
  };

  // Render Preview text replacing {{1}}, {{2}}, etc. with sample values
  const renderPreviewText = () => {
    return bodyText
      .replace(/\{\{1\}\}/g, 'Rahul Sharma')
      .replace(/\{\{2\}\}/g, 'IPO POS Salon')
      .replace(/\{\{3\}\}/g, 'FESTIVE50')
      .replace(/\{\{CustomerName\}\}/g, 'Rahul Sharma')
      .replace(/\{\{StoreName\}\}/g, 'IPO POS Salon')
      .replace(/\{\{InvoiceNumber\}\}/g, 'INV-2026-001')
      .replace(/\{\{BillAmount\}\}/g, '₹1,499.00')
      .replace(/\{\{OfferCode\}\}/g, 'FESTIVE50')
      .replace(/\{\{CurrentDate\}\}/g, new Date().toLocaleDateString('en-IN'));
  };

  if (!isOpen) return null;

  return (
    <div className="tmpl-editor-overlay">
      <div className="tmpl-editor-card" style={{ maxWidth: '960px', width: '95%' }}>
        {/* Header Bar */}
        <div className="tmpl-editor-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                {template?._id ? 'Edit WhatsApp Template' : 'Create New WhatsApp Template'}
              </h3>
              <span className="meta-connected-badge" style={{ fontSize: '11px', padding: '2px 8px' }}>
                <Icon icon="mdi:whatsapp" /> Meta Graph API Integrated
              </span>
            </div>
            <span style={{ fontSize: '12.5px', color: '#64748b', marginTop: '2px', display: 'block' }}>
              Template will be created simultaneously in IPO POS and submitted to Meta WABA for approval.
            </span>
          </div>
          <button
            style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b' }}
            onClick={onClose}
          >
            <Icon icon="mdi:close" />
          </button>
        </div>

        {/* Meta API Error Alert Banner */}
        {metaError && (
          <div style={{ margin: '14px 24px 0 24px', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '13px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <Icon icon="mdi:alert-circle" style={{ fontSize: '20px', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong>Meta API Rejection Error:</strong>
              <div style={{ marginTop: '2px', whiteSpace: 'pre-wrap' }}>{metaError}</div>
            </div>
          </div>
        )}

        {/* Body Container */}
        <div className="tmpl-editor-body">
          {/* Left Form Column */}
          <div className="tmpl-editor-form-col">
            {/* Template Name */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, marginBottom: '4px', color: '#334155' }}>
                Template Name * <span style={{ fontSize: '11px', fontWeight: 500, color: '#64748b' }}>(Lowercase & underscores only)</span>
              </label>
              <input
                type="text"
                value={templateName}
                onChange={handleNameChange}
                placeholder="e.g. festive_special_discount_2026"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px', fontFamily: 'monospace', fontWeight: 600 }}
              />
            </div>

            {/* Category & Language */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, marginBottom: '4px', color: '#334155' }}>
                  Meta Category *
                </label>
                <select
                  value={metaCategory}
                  onChange={(e) => setMetaCategory(e.target.value)}
                  style={{ width: '100%', padding: '8.5px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#0f172a' }}
                >
                  {META_CATEGORIES.map((cat) => (
                    <option key={cat.code} value={cat.code}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, marginBottom: '4px', color: '#334155' }}>
                  Template Language *
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  style={{ width: '100%', padding: '8.5px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#0f172a' }}
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Header Format */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, marginBottom: '4px', color: '#334155' }}>
                Header Format
              </label>
              <select
                value={headerType}
                onChange={(e) => {
                  setHeaderType(e.target.value);
                  setMediaType(e.target.value);
                }}
                style={{ width: '100%', padding: '8.5px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }}
              >
                <option value="NONE">None (Body Only)</option>
                <option value="TEXT">Text Header</option>
                <option value="IMAGE">Image Header</option>
                <option value="VIDEO">Video Header</option>
                <option value="DOCUMENT">Document (PDF) Header</option>
              </select>
            </div>

            {headerType === 'TEXT' && (
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#334155' }}>
                  Header Text
                </label>
                <input
                  type="text"
                  value={headerText}
                  onChange={(e) => setHeaderText(e.target.value)}
                  placeholder="e.g. 🎉 Special Offer for You!"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px' }}
                />
              </div>
            )}

            {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType) && (
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#334155' }}>
                  Sample {headerType} Header URL (Optional)
                </label>
                <input
                  type="text"
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  placeholder={`https://example.com/sample_${headerType.toLowerCase()}`}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px' }}
                />
              </div>
            )}

            {/* Message Body */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, marginBottom: '4px', color: '#334155' }}>
                Body Content *
              </label>
              <textarea
                rows={5}
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                placeholder="Type your message body text here... Use {{1}}, {{2}} for dynamic variables."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '13.5px',
                  fontFamily: 'inherit',
                  lineHeight: 1.5
                }}
              />
            </div>

            {/* Dynamic Variable Helper Chips */}
            <div style={{ marginBottom: '14px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>Insert Dynamic Variables:</span>
              <div className="placeholder-chips-box" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                {PLACEHOLDERS.map((ph) => (
                  <button
                    key={ph}
                    type="button"
                    style={{
                      padding: '3px 8px',
                      fontSize: '11.5px',
                      borderRadius: '6px',
                      border: '1px solid #bae6fd',
                      background: '#f0f9ff',
                      color: '#0369a1',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                    onClick={() => insertPlaceholder(ph)}
                  >
                    + {ph}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer Text */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#334155' }}>
                Footer Text (Optional)
              </label>
              <input
                type="text"
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                placeholder="e.g. Terms & Conditions apply. Reply STOP to unsubscribe."
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13.5px' }}
              />
            </div>

            {/* Action Buttons */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>Call to Action / Quick Reply Buttons (Max 3)</span>
                <button
                  type="button"
                  style={{
                    padding: '4px 10px',
                    fontSize: '12px',
                    borderRadius: '6px',
                    border: '1px solid #075e54',
                    background: '#e6f4f1',
                    color: '#075e54',
                    cursor: 'pointer',
                    fontWeight: 700
                  }}
                  onClick={addButton}
                >
                  + Add Button
                </button>
              </div>

              {buttons.map((btn, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '120px 1fr 32px',
                    gap: '8px',
                    marginBottom: '8px',
                    alignItems: 'center',
                    background: '#f8fafc',
                    padding: '8px 10px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <select
                    value={btn.type}
                    onChange={(e) => updateButton(idx, 'type', e.target.value)}
                    style={{ padding: '6px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: 600 }}
                  >
                    <option value="QUICK_REPLY">Quick Reply</option>
                    <option value="URL">Open URL</option>
                    <option value="PHONE_NUMBER">Call Phone</option>
                    <option value="COPY_CODE">Copy Code</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Button Label Text"
                    value={btn.text}
                    onChange={(e) => updateButton(idx, 'text', e.target.value)}
                    style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                  <button
                    type="button"
                    style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
                    onClick={() => removeButton(idx)}
                  >
                    <Icon icon="mdi:trash-can-outline" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Right Device Preview Column */}
          <div className="tmpl-editor-preview-col" style={{ width: '340px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '8px', display: 'block' }}>
              WhatsApp Phone Live Preview
            </span>

            <div className="mobile-device-frame" style={{ margin: 0 }}>
              <div className="device-screen">
                <div className="device-app-bar" style={{ background: '#075e54' }}>
                  <Icon icon="mdi:whatsapp" style={{ fontSize: '20px' }} />
                  <span>{templateName || 'new_template'}</span>
                </div>
                <div className="device-chat-body" style={{ background: '#efeae2', padding: '12px' }}>
                  <div className="chat-bubble" style={{ background: '#ffffff', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                    
                    {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType) && (
                      <div style={{ background: '#f8fafc', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', textAlign: 'center', marginBottom: '8px', color: '#64748b', fontSize: '11px', fontWeight: 600 }}>
                        <Icon icon="mdi:file-image" style={{ fontSize: '24px', color: '#075e54' }} />
                        <div>[{headerType} Header Attachment]</div>
                      </div>
                    )}

                    {headerType === 'TEXT' && headerText && (
                      <div style={{ fontWeight: 800, marginBottom: '6px', color: '#0f172a', fontSize: '13.5px' }}>{headerText}</div>
                    )}

                    <div style={{ fontSize: '13px', color: '#1e293b', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                      {renderPreviewText()}
                    </div>

                    {footerText && (
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '8px', fontStyle: 'italic', borderTop: '1px dashed #cbd5e1', paddingTop: '4px' }}>
                        {footerText}
                      </div>
                    )}

                    {buttons.length > 0 && (
                      <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {buttons.map((b, i) => (
                          <div key={i} style={{ background: '#ffffff', border: '1px solid #e2e8f0', color: '#00a884', fontWeight: 700, fontSize: '12px', padding: '6px', borderRadius: '4px', textAlign: 'center' }}>
                            {b.text || 'Action Button'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#f8fafc' }}>
          <button
            style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#ffffff', cursor: 'pointer', fontWeight: 600 }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            style={{
              padding: '9px 22px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)',
              color: '#ffffff',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onClick={handleSave}
            disabled={loading}
          >
            <Icon icon={loading ? 'mdi:loading' : 'mdi:cloud-upload-outline'} className={loading ? 'spin' : ''} />
            {loading ? 'Submitting to Meta WABA...' : template?._id ? 'Update Template' : 'Submit & Save to Meta'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditorModal;
